// Third-party
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import debounce from 'lodash/debounce';
import { SortingRule } from 'react-table';
import classNames from 'classnames';

// Actions
import { togglePortfolioEditor, resetCurrentContact } from '@/redux/modules/common/actionCreators';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';
import { loadPortfolioForEdit, resetPortfolio, savePortfolio } from '@/redux/modules/edit-portfolio';
import { addPortfoliosToComparison, loadNewPortfolio, ADD_PORTFOLIOS_TO_COMPARISON, createComparison } from '@/redux/modules/comparison';
import { loadContactPortfolios, getContactPortfolios, initLoadingPortfolios, loadFactsetAccountLookup } from '@/redux/modules/contacts';
import { ReduxState } from '@/redux/modules/types';
import { ComparisonsListState } from '@/redux/modules/types/comparison';
import { getModelsPortfolios } from '@/redux/modules/portfolios';

// Enums
import { COMPARISONS_FILTER } from '../comparison/ComparisonHome/ComparisonsFilterNavigation';
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Types
import GenericContact from '@/types/generic-contact';
import User from '@/types/user';
import Comparison from '@/types/comparison';
import ReducedComparison from '@/types/reduced-comparison';
import Portfolio from '@/types/portfolio';
import Contact from '@/types/contact';
import ComparisonItem from '@/types/comparison-item';
import { SortingField, modelType } from '@/types/data';
import ThemeMode from '@/types/theme-mode';

// Components
import { LoadingSpinner } from '@/views/components/common';
import DelegateBanner from '../auth/DelegateBanner';
import { AnnouncementBanner } from '../announcements';
import Banner from './Banner';
import HowItWorks from './HowItWorks';
import ComparisonsList from '../comparison/ComparisonHome/ComparisonsList';
import { CreateAnalysisModal, LoadModelsModal, ContactPortfoliosModal, EditPortfolioModal } from '@/views/components/modals';

// Utils
import { MAX_VISIBLE_COMPARISONS, NAV_PATHS } from '@/utils/constants';
import { formatMessage } from '@/utils/i18n';
import { getContactLayout } from '@/utils/contacts';
import heapUtils, { HEAP_CUSTOM_EVENTS } from '@/utils/3rd-party/heap';
import disclaimers from '@/utils/disclaimers';
import { clonePortfolioAsProxy } from '@/utils/portfolio/utils';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import PaginatedPortfolios from '@/types/paginated-portfolios';
import UserPreferences from '@/types/user-preferences';
import { Header } from '@/views/components/layout';

type DispatchProps = {
  savePortfolio: (portfolio: Portfolio, contact?: Contact) => Promise<Portfolio>;
  addPortfoliosToComparison: (portfolios: Portfolio[]) => Promise<void>;
  togglePortfolioEditor: (show: boolean) => void;
  resetPortfolio: () => void;
  loadNewPortfolio: (contact?: GenericContact, createItem?: boolean) => Promise<void>;
  loadPortfolioForEdit: (portfolio: Portfolio, comparisonItem?: ComparisonItem, isPinned?: boolean) => Promise<void>;
  createComparison: (contact?: GenericContact) => Promise<Comparison>;
  initLoadingPortfolios: () => void;
  getContactPortfolios: (query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => Promise<Portfolio[]>;
  loadContactPortfolios: (contact: GenericContact) => Promise<void>;
  loadFactsetAccountLookup: () => void;
  resetCurrentContact: () => void;
  getModelsPortfolios: (type?: modelType, query?: string, sort?: SortingRule[], nextToken?: string) => Promise<Portfolio[]>;
};

type StateProps = {
  editPortfolioModalIsVisible: boolean;
  isComparingPortfolios: boolean;
  selectedContact?: {
    contact: GenericContact;
    portfolios?: Portfolio[];
    hasMore: {
      portfolios: boolean;
    };
    nextContactPortfoliosToken?: string;
    nextContactComparisonsToken?: string;
  };
  userPreferences?: UserPreferences;
};

type Props = RouteComponentProps &
  DispatchProps &
  StateProps & {
    user: User;
    comparisons: {
      [key in COMPARISONS_FILTER]: ComparisonsListState;
    };
    comparisonsFilter: COMPARISONS_FILTER;
    loadComparisons: (query?: string, useNextToken?: boolean) => Promise<ReducedComparison[]>;
    onOpenComparison: (comparison: ReducedComparison) => void;
    onSaveComparison: (id: string, name: string, contact?: GenericContact) => Promise<Comparison>;
    onDeleteComparison: (comparison: ReducedComparison) => void;
    isLoadingRecentComparisons?: boolean;
  };

const Landing: React.FC<Props> = ({
  history,
  user,
  comparisons,
  comparisonsFilter,
  isComparingPortfolios,
  isLoadingRecentComparisons,
  editPortfolioModalIsVisible,
  location,
  selectedContact,
  loadComparisons,
  onOpenComparison,
  onSaveComparison,
  onDeleteComparison,
  savePortfolio,
  addPortfoliosToComparison,
  togglePortfolioEditor,
  resetPortfolio,
  loadNewPortfolio,
  loadPortfolioForEdit,
  initLoadingPortfolios,
  getContactPortfolios,
  loadContactPortfolios,
  loadFactsetAccountLookup,
  resetCurrentContact,
  getModelsPortfolios,
  userPreferences,
}: Props) => {
  const [showHowWorks, setShowHowWorks] = useState<boolean>(false);
  const [showCreateAnalysisModal, setShowCreateAnalysisModal] = useState<boolean>(false);
  const [showEditPortfolioModal, setShowEditPortfolioModal] = useState<boolean>(false);
  const [showContactPortfoliosModal, setShowContactPortfoliosModal] = useState<boolean>(false);
  const [showLoadModelsModal, setShowLoadModelsModal] = useState<boolean>(false);
  const [pendingPortfolios, setPendingPortfolios] = useState<Array<Portfolio>>([]);
  const [queries, setQueries] = useState({ portfolios: '', contactPortfolios: '' });

  useEffect(() => {
    if (pendingPortfolios.length > 0) {
      handlePendingPortfolios();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPortfolios]);

  const fetchModelsResults = debounce(() => {
    const promises: Promise<Portfolio[] | PaginatedPortfolios>[] = [];

    if (user && userHasPermissionEnabled(user, PERMISSIONS.USER_MODELS)) {
      promises.push(getModelsPortfolios(modelType.User));
    }

    if (user && user.enterprise && userHasPermissionEnabled(user, PERMISSIONS.ENTERPRISE_MODELS_LIST)) {
      promises.push(getModelsPortfolios(modelType.Enterprise));
    }

    Promise.all(promises);
  }, 300);

  const fetchContactPortfoliosResults = debounce((query?: string, sort?: SortingField[], shouldFetchNext?: boolean) => {
    getContactPortfolios(query, sort, shouldFetchNext);
  }, 300);

  const comparisonsList = comparisons[comparisonsFilter] && comparisons[comparisonsFilter].list;

  const onStart = () => {
    const hasNewPortfolioPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISONS_NEW_PORTFOLIO);
    const hasClientAccountPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISONS_CLIENT_ACCOUNT);
    const hasModelPortfolioPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISONS_MODEL_PORTFOLIO);

    if (hasNewPortfolioPermission && !hasClientAccountPermission && !hasModelPortfolioPermission) {
      return handleShowEditPortfolioModal();
    }

    if (!hasNewPortfolioPermission && hasClientAccountPermission && !hasModelPortfolioPermission) {
      return setShowContactPortfoliosModal(true);
    }

    if (!hasNewPortfolioPermission && !hasClientAccountPermission && hasModelPortfolioPermission) {
      return setShowLoadModelsModal(true);
    }

    setShowCreateAnalysisModal(true);
  };

  const toggleHowWorksSection = () => {
    setShowHowWorks(!showHowWorks);
  };

  const hideVisibleModal = () => {
    if (showLoadModelsModal) {
      setShowLoadModelsModal(false);
    } else if (showContactPortfoliosModal) {
      setShowContactPortfoliosModal(false);
    }
  };

  const handleAddPortfoliosToComparison = async (portfolios: Portfolio[]) => {
    if (portfolios.some(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p))) {
      setPendingPortfolios(portfolios);
      hideVisibleModal();

      return Promise.resolve();
    }

    return addPortfoliosToComparison(portfolios).then(() => history.push(NAV_PATHS.COMPARISON, { from: location }));
  };

  const handleHideEditPortfolioModal = () => {
    setShowEditPortfolioModal(false);
    resetPortfolio();
    togglePortfolioEditor(false);
  };

  const handleShowEditPortfolioModal = async (portfolio?: Portfolio) => {
    resetPortfolio();

    if (portfolio?._id === undefined) {
      loadNewPortfolio();
    } else {
      loadPortfolioForEdit(portfolio);
    }

    setShowEditPortfolioModal(true);
    togglePortfolioEditor(true);
  };

  const handleSavePortfolio = (newPortfolio: Portfolio): Promise<Portfolio> => {
    const id = newPortfolio._id;

    return savePortfolio(newPortfolio).then(portfolio => {
      heapUtils.track(HEAP_CUSTOM_EVENTS.NEW_COMPARISON);

      if (pendingPortfolios.length) {
        setPendingPortfolios(pendingPortfolios.filter(p => p._id !== id).concat([portfolio]));
      } else {
        addPortfoliosToComparison([portfolio]).then(() => {
          handleHideEditPortfolioModal();
          history.push(NAV_PATHS.COMPARISON, { from: location });
        });
      }

      return portfolio;
    });
  };

  const handlePendingPortfolios = () => {
    if (pendingPortfolios && pendingPortfolios.length) {
      const portfolioWithMissingHolding = pendingPortfolios.find(p => disclaimers.portfolioHasMissingOrInvalidHoldings(p));

      if (portfolioWithMissingHolding) {
        pendingPortfolios.splice(pendingPortfolios.indexOf(portfolioWithMissingHolding), 1);
        const portfolioClone = clonePortfolioAsProxy(portfolioWithMissingHolding);

        if (user && userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIO_UNITS_MODE)) {
          portfolioClone.allocationMode = HoldingAllocationMode.Units;
        }

        handleShowEditPortfolioModal(portfolioClone);
      } else {
        setPendingPortfolios([]);
        handleAddPortfoliosToComparison(pendingPortfolios);
      }
    }
  };

  const onSearchContactPortfolios = (query: string, sort?: SortingField[], shouldFetchNext?: boolean) => {
    initLoadingPortfolios();
    setQueries(prevState => ({ ...prevState, contactPortfolios: query }));
    fetchContactPortfoliosResults(query, sort, shouldFetchNext);
  };

  const onLoadMoreContactPortfolios = (sort?: SortingField[]) => {
    if (!selectedContact) return;
    getContactPortfolios(queries.contactPortfolios, sort);
  };

  return (
    <>
      {userHasPermissionEnabled(user, PERMISSIONS.FACTSET_MODE_HEADER) && <Header />}
      {!userHasPermissionEnabled(user, PERMISSIONS.FACTSET_MODE_HEADER) && (
        <>
          <DelegateBanner />
          <AnnouncementBanner />
        </>
      )}

      <div className={classNames('landing', `landing-theme-${userPreferences?.theme || ThemeMode.LIGHT}`)}>
        {comparisonsList && (
          <div className="container fact-set-comparisons-list">
            <div className="row">
              <div className="col-12">
                <ComparisonsList
                  user={user}
                  comparisons={comparisons}
                  comparisonsFilter={comparisonsFilter}
                  onCreateComparison={onStart}
                  loadComparisons={loadComparisons}
                  onOpenComparison={onOpenComparison}
                  onSaveComparison={onSaveComparison}
                  onDeleteComparison={onDeleteComparison}
                  options={{
                    showCreateComparison: true,
                    tableIsSortable: true,
                    showHeader: false,
                    hideEmptyStateCard: true,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {!isLoadingRecentComparisons && (
          <>
            {comparisonsList && comparisonsList.length <= MAX_VISIBLE_COMPARISONS && (
              <Banner
                onCreateAnalysis={onStart}
                hasComparisons={!!(comparisonsList && comparisonsList.length)}
                showMore={!showHowWorks}
                onClickShow={toggleHowWorksSection}
              />
            )}

            {(!(comparisonsList && comparisonsList.length) || showHowWorks) && <HowItWorks onGetStarted={onStart} />}
          </>
        )}

        {showCreateAnalysisModal && (
          <CreateAnalysisModal
            show
            user={user}
            onClose={() => setShowCreateAnalysisModal(false)}
            onClickPortfolio={() => {
              setShowCreateAnalysisModal(false);
              handleShowEditPortfolioModal();
            }}
            onClickClient={() => {
              setShowCreateAnalysisModal(false);
              setShowContactPortfoliosModal(true);
            }}
            onClickModel={() => {
              fetchModelsResults();
              setShowCreateAnalysisModal(false);
              setShowLoadModelsModal(true);
            }}
            onClickFactsetPortfolios={() => {
              loadFactsetAccountLookup();
              setShowCreateAnalysisModal(false);
            }}
          />
        )}
      </div>

      {editPortfolioModalIsVisible && (
        <EditPortfolioModal
          show={showEditPortfolioModal}
          onSave={handleSavePortfolio}
          onCancel={handleHideEditPortfolioModal}
          saveLabel={formatMessage('Comparison.AddToComparison')}
          saveNotification={formatMessage('HoldingsEditor.Created.PORTFOLIO')}
          onExited={handlePendingPortfolios}
        />
      )}

      {showContactPortfoliosModal && (
        <ContactPortfoliosModal
          show
          isComparing={isComparingPortfolios}
          query={queries.contactPortfolios}
          selectedContact={selectedContact}
          onCancel={() => setShowContactPortfoliosModal(false)}
          onSearchContactPortfolios={onSearchContactPortfolios}
          loadContactPortfolios={loadContactPortfolios}
          loadMoreContactPortfolios={onLoadMoreContactPortfolios}
          addPortfoliosToComparison={handleAddPortfoliosToComparison}
          resetCurrentContact={resetCurrentContact}
          layout={getContactLayout(user)}
        />
      )}

      {showLoadModelsModal && (
        <LoadModelsModal show user={user} onCancel={() => setShowLoadModelsModal(false)} addPortfoliosToComparison={handleAddPortfoliosToComparison} />
      )}

      {isLoadingRecentComparisons && <LoadingSpinner />}
    </>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  editPortfolioModalIsVisible: state.comparison.portfolioEditor.isVisible,
  isComparingPortfolios: selectIsLoadingEvery(state, [ADD_PORTFOLIOS_TO_COMPARISON.type]),
  selectedContact: state.contacts.current,
  userPreferences: state.auth.user?.preferences,
});

export default connect(mapStateToProps, {
  addPortfoliosToComparison,
  savePortfolio,
  togglePortfolioEditor,
  resetPortfolio,
  loadNewPortfolio,
  loadPortfolioForEdit,
  createComparison,
  initLoadingPortfolios,
  getContactPortfolios,
  loadContactPortfolios,
  loadFactsetAccountLookup,
  resetCurrentContact,
  getModelsPortfolios,
})(Landing);
